<template>
  <div class="mx-6" v-resize="onResize">
    <div :class="$vuetify.breakpoint.xs ? 'text-h6 mt-4' : 'text-h5'">
      <div class="d-flex mr-5">
        <div class="d-flex">
          <v-icon class="mt-0" :large="!$vuetify.breakpoint.xs">mdi-variable</v-icon>
          <div class="mt-2 pt-3 ml-2">Fixed Param</div>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                  color="success"
                  class="ml-4 mt-4"
                  fab
                  v-on="on"
                  small
                  @click="downloadCsv()">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
            </template>
            <span>Download to Excel</span>
          </v-tooltip>
        </div>
        <v-spacer /><v-spacer />
        <v-text-field
          class="pt-5 mt-0 mr-4"
          v-model="search"
          clearable
          label="Search"
          type="text"
          prepend-icon="mdi-magnify"
        />
        <v-btn
          class="mt-3"
          color="primary"
          :large="$vuetify.breakpoint.xs ? false : true"
          @click="addItem()"
        >
          <v-icon>mdi-plus</v-icon>
          <div v-show="$vuetify.breakpoint.xs ? false : true">
            Add
          </div>
        </v-btn>
      </div>
    </div>
    <div>
      <v-row class="justify-center">
          <v-col cols="2">
            <v-select v-model="selectedFarms"
              class="mr-4"
              label="Farm"
              chips
              small-chips
              multiple
              clearable
              :items="farmList"
            />
         </v-col>
          <v-col cols="2">
            <v-select v-model="selectedLines"
              label="Line"
              chips
              small-chips
              multiple
              clearable
              :items="lineList"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Defect Rate"
              chips
              small-chips
              multiple
              clearable
              v-model="selectedDefectRates"
              :items="defectRateList"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Female Floor"
              chips
              small-chips
              multiple
              clearable
              v-model="selectedFemaleFloors"
              :items="femaleFloorList"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Male Floor"
              chips
              small-chips
              multiple
              clearable
              v-model="selectedMaleFloors"
              :items="maleFloorList"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Mortality Rate"
              chips
              small-chips
              multiple
              clearable
              v-model="selectedMortalityRates"
              :items="mortalityRateList"
            />
          </v-col>
      </v-row>
    </div>
    <v-data-table
      :height="tableHeight"
      class="mt-3 mx-5"
      fixed-header
      multi-sort
      :search="search"
      :headers="tableHeaders"
      :items="filteredItems"
      no-data-text="No Data Available">
        <template v-slot:[`item.isEnabled`]="{ item }">
          <div class="d-flex justify-center">
            <v-icon v-if="item.isEnabled">mdi-checkbox-marked-outline</v-icon>
            <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
          </div>
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <iag-action-buttons
            primary-button-icon="mdi-square-edit-outline"
            @primary-button-clicked="editItem(item)"
            secondary-button-icon="mdi-trash-can-outline"
            @secondary-button-clicked="confirmDeleteItem(item)"/>
        </template>
    </v-data-table>

    <v-dialog v-model="showDeleteDialog" persistent max-width="320">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Delete
        </v-card-title>
        <v-card-text>
            Ok to Delete Id: {{itemToDelete.Id}}?
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer />
          <iag-action-buttons
            primary-button-text="Yes"
            secondary-button-text="No"
            @primary-button-clicked="deleteItem()"
            @secondary-button-clicked="showDeleteDialog = false" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-form ref="form" v-model="validForm">
      <v-dialog v-model="showChangeDialog" 
          scrollable 
          persistent
          max-width="600px">
        <v-card>
          <v-card-title class="text-h5">
              {{isAdd ? 'Add' : 'Edit'}} Fixed Parm {{isAdd ? '': `(Id: ${editedItem.Id})`}}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.Farm"
                  :items="farmList"
                  label="Farm"
                  @blur="editedItem.Farm=toAlphaUpperCase(editedItem.Farm)"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.Line"
                  :items="lineList"
                  label="Line"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="editedItem.DefectRate"
                  :items="defectRateList"
                  label="Defect Rate"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.FemaleFloor"
                  :items="femaleFloorList"
                  label="Female Floor"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.MaleFloor"
                  :items="maleFloorList"
                  label="Male Floor"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.MortalityRate"
                  :items="mortalityRateList"
                  label="Mortality Rate"
                  :rules="[rules.required, rules.numeric]"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.Comment"
                  label="Comment" />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions ref="saveAction" class="pb-3">
            <v-spacer />
            <iag-action-buttons
              :primary-button-disabled="!validForm"
              primary-button-text="Save"
              secondary-button-text="Cancel"
              @primary-button-clicked="saveItem()"
              @secondary-button-clicked="closeChangeDialog()"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-form>

    <v-overlay :value="isBusy">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import ActionButtons from './../components/ActionButtons.vue'
import iagApi from '../api/iagApi'
import json2csv from "../utils/json2csv"

export default {
    components: {
    'iag-action-buttons': ActionButtons,
  },
  data() {
    return {
      items: [],
      editedItem: {},
      isBusy: true,
      isAdd: false,
      itemToDelete: {},
      rules: null,
      search: '',
      selectedFarms: [],
      selectedLines: [],
      selectedDefectRates: [],
      selectedFemaleFloors: [],
      selectedMaleFloors: [],
      selectedMortalityRates: [],
      showChangeDialog: false,
      showDeleteDialog: false,
      windowSize: {x: 0,y: 0},
      validForm: true
    }
  },
  mounted () {
    this.onResize();
  },
  async created() {
    try {
      this.isBusy = true;
      this.rules = { ...this.$root.validationRules };
      await this.getFixedParamData();
    }
    finally {
      this.isBusy = false;
    }
  },
  computed: {
    filteredItems() {
      return this.items.filter(item =>
        {
          let showItem =
            (this.selectedFarms.length == 0 || this.selectedFarms.includes(item.Farm)) &&
            (this.selectedLines.length == 0 || this.selectedLines.includes(item.Line)) &&
            (this.selectedDefectRates.length == 0 || this.selectedDefectRates.includes(item.DefectRate)) &&
            (this.selectedFemaleFloors.length == 0 || this.selectedFemaleFloors.includes(item.FemaleFloor)) &&
            (this.selectedMaleFloors.length == 0 || this.selectedMaleFloors.includes(item.MaleFloor)) &&
            (this.selectedMortalityRates.length == 0 || this.selectedMortalityRates.includes(item.MortalityRate))
          ;
          return showItem;
        }
      );
    },
    tableHeaders() {
      return [
        { text: 'Id', value: 'Id', sortable: true, align: 'center' },
        { text: 'Farm', value: 'Farm', sortable: true, align: 'center' },
        { text: 'Line', value: 'Line', sortable: true, align: 'center' },
        { text: 'Defect Rate', value: 'DefectRate', sortable: true, align: 'center' },
        { text: 'Female Floor', value: 'FemaleFloor', sortable: true, align: 'center' },
        { text: 'Male Floor', value: 'MaleFloor', sortable: true, align: 'center' },
        { text: 'Mortality Rate', value: 'MortalityRate', sortable: true, align: 'center' },
        { text: 'Comment', value: 'Comment', sortable: true, align: 'center' },
        { text: 'Actions', value: 'action', sortable: false, align: 'center' }
      ];
    },
    farmList() {
      let uniqueItems = [...new Set(this.items.map(item => item.Farm))];
      this.sortArray(uniqueItems);
      return uniqueItems;
    },
    lineList() {
      let uniqueItems = [...new Set(this.items.map(item => item.Line))];
      this.sortNumArray(uniqueItems);
      return uniqueItems;
    },
    maleFloorList() {
      let uniqueItems = [...new Set(this.items.map(item => item.MaleFloor))];
      this.sortNumArray(uniqueItems);
      return uniqueItems;
    },
    defectRateList() {
      let uniqueItems = [...new Set(this.items.map(item => item.DefectRate))];
      this.sortNumArray(uniqueItems);
      return uniqueItems;
    },
    femaleFloorList() {
      let uniqueItems = [...new Set(this.items.map(item => item.FemaleFloor))];
      this.sortNumArray(uniqueItems);
      return uniqueItems;
    },
    mortalityRateList() {
      let uniqueItems = [...new Set(this.items.map(item => item.MortalityRate))];
      this.sortNumArray(uniqueItems);
      return uniqueItems;
    },
    tableHeight() {
      let tableSize = this.windowSize.y - 295;

      if (this.$vuetify.breakpoint.name == 'xs' || this.$vuetify.breakpoint.name === 'sm') {
        tableSize += 4; // v-app-bar shorter for xs and sm
      }
      if (tableSize < 200) tableSize = 200;
      return tableSize;
    },
 },
  methods: {
    addItem() {
      this.$refs.form.resetValidation();
      this.isAdd = true;
      this.editedItem = {
      };
      this.showChangeDialog = true;
    },
    confirmDeleteItem(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },
    closeChangeDialog() {
      this.showChangeDialog = false;
    },
    async deleteItem() {
      try {
        this.isBusy = true;
        this.showDeleteDialog = false;
        await iagApi.deleteFixedParam(this.itemToDelete.Id);
        await this.getFixedParamData();
      } catch (error) {
         this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    downloadCsv() {
      try {
        const items = this.items.map(x => {
          return {
            Id: x.Id,
            Farm: x.Farm,
            Line: x.Line,
            DefectRate: x.DefectRate,
            FemaleFloor: x.FemaleFloor,
            MaleFloor: x.MaleFloor,
            MortalityRate: x.MortalityRate,
            Comment: x.Comment ? x.Comment : ''
          }
        });
        var csv = json2csv.convert(items);
        const fileName = `Iag-FixedParam.csv`;
        this.$root.downloadCsvLocal(fileName, csv);
      }
      catch (error) {
        this.$root.showNoticeError(error);
      }
    },
    editItem(item) {
      this.$refs.form.resetValidation();
      this.isAdd = false;
      this.editedItem = {...item}

      this.showChangeDialog = true;
    },
    async getFixedParamData() {
      try {
        this.isBusy = true;
        let response = await iagApi.getFixedParams();
        this.items = await response.json();
      } catch (error) {
         this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight }
    },
    async saveItem() {
      try {
        this.isBusy = true;

        //* Change focus away from v-combobox values and sleep briefly. This is
        //* to allow stupid v-combobox to set value if user typed a new value.
        this.$refs.saveAction.focus();
        await this.sleep(100);

        this.closeChangeDialog();

        let payload = {...this.editedItem};
        let response = await iagApi.postFixedParam(payload);
        console.log(response);

        await this.getFixedParamData();
      } catch (error) {
         this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    sortArray(arr) {
      let sortedArray = arr.sort();
      return sortedArray;
    },
    sortNumArray(arr) {
      arr.sort((x,y) => {
        let v = parseFloat(x) - parseFloat(y);
        return v;
      });
    },
    toAlphaUpperCase(value) {
      let testVal = (value || "");
      const regex = /[^A-Za-z]/g;
      testVal = testVal.replace(regex, "");
      return testVal.toUpperCase();
    }
  }
}
</script>